import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { Loader2, Shield, Zap, Check, ArrowLeft, Gift } from "lucide-react";
import { usePartnerBranding } from "../hooks/usePartnerBranding";

const COUNTRIES = [
  { code: "KE", name: "Kenya" },
  { code: "UG", name: "Uganda" },
  { code: "TZ", name: "Tanzania" },
  { code: "RW", name: "Rwanda" },
  { code: "NG", name: "Nigeria" },
];

const BUSINESS_TYPES = [
  "retail", "wholesale", "food_beverage", "salon_beauty", "transport_logistics",
  "agriculture", "services", "other",
];

export default function Signup() {
  const { t, i18n } = useTranslation();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refFromUrl = (searchParams.get("ref") || "").toUpperCase();
  const branding = usePartnerBranding();
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({
    business_name: "", owner_name: "", phone: "+254",
    email: "", password: "", country: "KE",
    business_type: "retail", language: i18n.resolvedLanguage || "en",
    registration_number: "",
    referrer_code: refFromUrl,
  });

  useEffect(() => {
    if (refFromUrl) {
      setForm((s) => ({ ...s, referrer_code: refFromUrl }));
    }
  }, [refFromUrl]);

  function update(k, v) {
    setForm((s) => ({ ...s, [k]: v }));
  }

  async function onSubmit(e) {
    e.preventDefault();
    setErr("");
    if (form.password.length < 8) {
      setErr("Password must be at least 8 characters.");
      return;
    }
    setSubmitting(true);
    try {
      await register({
        ...form,
        language: i18n.resolvedLanguage || form.language,
        partner_slug: branding.slug || undefined,
      });
      navigate("/merchant", { replace: true });
    } catch (e) {
      setErr(e.message || "Sign-up failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex bg-[#f4f2eb]" data-testid="signup-page">
      {/* Left brand panel */}
      <div
        className="hidden lg:flex lg:w-1/2 relative"
        style={{
          backgroundImage:
            "linear-gradient(rgba(17,66,50,0.82), rgba(17,66,50,0.95)), url('https://images.unsplash.com/photo-1761370980657-22586ea44093?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="m-auto p-12 max-w-lg text-[#f4f2eb]">
          <Link to="/" className="flex items-center gap-3 mb-10" data-testid="signup-brand-home">
            <div className="relative">
              <Shield className="w-8 h-8" strokeWidth={1.5} />
              <span className="absolute -bottom-1 -right-1 w-2.5 h-2.5 rounded-full bg-[#c15337]" />
            </div>
            <div className="font-heading text-2xl font-bold tracking-tight">Nyoksure</div>
          </Link>
          <h1 className="font-heading text-4xl xl:text-5xl font-bold leading-[1.05] mb-6">
            Get covered in <span className="text-[#d49a36]">60 seconds.</span>
          </h1>
          <p className="text-[#f4f2eb]/85 text-base leading-relaxed mb-8">
            Embedded cyber micro-insurance for African merchants. Weekly premium
            from KES 99, FlashClaim payouts in seconds, IRA-aligned.
          </p>
          <ul className="space-y-3 text-sm text-[#f4f2eb]/90">
            {[
              "No paperwork — just your phone number",
              "Cover up to KES 1,000,000",
              "10% loyalty rebate after 4 clean weeks",
              "Bilingual support — English & Kiswahili",
            ].map((b) => (
              <li key={b} className="flex items-center gap-2">
                <Check className="w-4 h-4 text-[#d49a36]" strokeWidth={2.5} />
                {b}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Right form panel */}
      <div className="w-full lg:w-1/2 flex flex-col">
        <div className="flex items-center justify-between px-6 lg:px-10 py-5">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-[#5c6360] hover:text-[#114232]"
                data-testid="signup-back-link">
            <ArrowLeft className="w-3.5 h-3.5" /> {t("common.back")}
          </Link>
          <LanguageSwitcher compact />
        </div>

        <div className="flex-1 flex items-center justify-center px-6 lg:px-10 pb-10">
          <form onSubmit={onSubmit} className="w-full max-w-md space-y-5"
                data-testid="signup-form">
            <div className="space-y-1">
              <div className="text-[11px] uppercase tracking-[0.22em] text-[#c15337] font-bold">
                {branding.name ? `${branding.name} · Powered by Nyoksure` : "Nyoksure Portal"}
              </div>
              <h2 className="font-heading text-3xl font-bold" style={branding.primary ? { color: branding.primary } : { color: "#114232" }}>
                {t("auth.signup")}
              </h2>
              <p className="text-sm text-[#5c6360]">
                Already have an account?{" "}
                <Link to={branding.slug ? `/login?partner=${branding.slug}` : "/login"} className="underline text-[#114232]" data-testid="signup-to-login">
                  {t("auth.login")}
                </Link>
              </p>
              {branding.partner && (
                <div className="flex items-center gap-2 mt-2" data-testid="partner-branding-pill-signup">
                  {branding.logoUrl ? (
                    <img src={branding.logoUrl} alt={branding.name} className="h-6 w-auto" />
                  ) : (
                    <div className="w-6 h-6 rounded" style={{ backgroundColor: branding.primary }} />
                  )}
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-[#5c6360]">
                    Signing up with {branding.name}
                  </span>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2 space-y-1.5">
                <Label htmlFor="business_name">Business name</Label>
                <Input id="business_name" required value={form.business_name}
                       onChange={(e) => update("business_name", e.target.value)}
                       data-testid="signup-business-name"
                       placeholder="Wanjiru's Tech Shop"
                       className="bg-white border-[#114232]/15 focus-visible:ring-[#114232]/20" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="owner_name">Owner</Label>
                <Input id="owner_name" required value={form.owner_name}
                       onChange={(e) => update("owner_name", e.target.value)}
                       data-testid="signup-owner-name"
                       placeholder="Mary Wanjiru"
                       className="bg-white border-[#114232]/15" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="phone">Phone (E.164)</Label>
                <Input id="phone" required value={form.phone} type="tel"
                       onChange={(e) => update("phone", e.target.value)}
                       data-testid="signup-phone"
                       placeholder="+254712345678"
                       className="bg-white border-[#114232]/15 font-mono" />
              </div>
              <div className="sm:col-span-2 space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input id="email" required value={form.email} type="email"
                       onChange={(e) => update("email", e.target.value)}
                       data-testid="signup-email"
                       placeholder="wanjiru@example.co.ke"
                       className="bg-white border-[#114232]/15" />
              </div>
              <div className="sm:col-span-2 space-y-1.5">
                <Label htmlFor="password">Password (≥ 8 chars)</Label>
                <Input id="password" required value={form.password} type="password"
                       onChange={(e) => update("password", e.target.value)}
                       data-testid="signup-password"
                       minLength={8}
                       className="bg-white border-[#114232]/15" />
              </div>
              <div className="space-y-1.5">
                <Label>Country</Label>
                <select value={form.country}
                        onChange={(e) => update("country", e.target.value)}
                        data-testid="signup-country"
                        className="w-full h-10 px-3 rounded-md border border-[#114232]/15 bg-white text-sm">
                  {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label>Business type</Label>
                <select value={form.business_type}
                        onChange={(e) => update("business_type", e.target.value)}
                        data-testid="signup-business-type"
                        className="w-full h-10 px-3 rounded-md border border-[#114232]/15 bg-white text-sm">
                  {BUSINESS_TYPES.map((b) => (
                    <option key={b} value={b}>{b.replace(/_/g, " ")}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2 space-y-1.5">
                <Label htmlFor="reg_no">Registration # (optional)</Label>
                <Input id="reg_no" value={form.registration_number}
                       onChange={(e) => update("registration_number", e.target.value)}
                       data-testid="signup-reg-number"
                       placeholder="PVT-XXXXX-2024"
                       className="bg-white border-[#114232]/15 font-mono" />
              </div>
              <div className="sm:col-span-2 space-y-1.5">
                <Label htmlFor="ref_code" className="flex items-center gap-1.5">
                  <Gift className="w-3.5 h-3.5 text-[#8a5a16]" /> Referral code (optional)
                </Label>
                <Input id="ref_code" value={form.referrer_code}
                       onChange={(e) => update("referrer_code", e.target.value.toUpperCase())}
                       data-testid="signup-referrer-code"
                       placeholder="e.g. AB12CD"
                       maxLength={8}
                       className="bg-white border-[#114232]/15 font-mono tracking-widest" />
                {form.referrer_code && (
                  <p className="text-[11px] text-[#8a5a16]" data-testid="signup-ref-applied">
                    You and the referrer both get 1 free week when you pay your first premium.
                  </p>
                )}
              </div>
            </div>

            {err && (
              <div data-testid="signup-error"
                   className="text-sm text-[#c15337] bg-[#c15337]/8 border border-[#c15337]/20 px-3 py-2 rounded-md">
                {err}
              </div>
            )}

            <Button type="submit" disabled={submitting}
                    className="w-full btn-baobab h-11 rounded-full hover:bg-[var(--baobab-hover)]"
                    data-testid="signup-submit-btn">
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create account
            </Button>

            <p className="text-[11px] text-[#5c6360] text-center">
              By creating an account you agree to the Key Facts Statement and
              ODPC data-processing consent.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
